<script setup lang="ts">
interface PaymentIntent {
  client_secret: string;
  amount?: number;
  status?: string;
  [key: string]: unknown;
}

interface Props {
  bookDelivery?: boolean;
  paymentIntent: PaymentIntent;
  isPremiumGroupOrder?: boolean;
  showCancel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  bookDelivery: true,
  isPremiumGroupOrder: false,
  showCancel: false,
});

const emit = defineEmits<{
  "payment:confirmed": [paymentIntentId: string];
  "payment:cancel": [];
  paymentSuccess: [paymentIntent: unknown];
  paymentError: [error: string];
}>();

const config = useRuntimeConfig();
const ordersStore = useOrdersStore();

const stripeElementsRef = ref<HTMLElement | null>(null);
const stripeReady = ref(false);
const error = ref("");

let stripe: unknown = null;
let elements: unknown = null;
let paymentElement: unknown = null;

const appearance = {
  theme: "stripe",
  variables: {
    borderRadius: "0.4rem",
    fontFamily: "'GalanoGrotesqueMedium', sans-serif",
    colorTextPlaceholder: "#a9a9a9",
  },
  rules: {
    ".Input": {
      padding: "0.8rem",
      fontSize: "0.8rem",
      fontFamily: "'GalanoGrotesqueMedium', sans-serif",
      border: "2px solid #a9a9a9",
      marginBottom: "0.6rem",
      lineHeight: "0.875",
    },
    ".Tab": {
      border: "2px solid #a9a9a9",
      marginBottom: "0.8rem",
    },
    ".Error": {
      padding: "7px 0 0",
      fontSize: "12px",
      fontFamily: "'GalanoGrotesqueMedium', sans-serif",
    },
    ".Label": {
      fontSize: "1rem",
      color: "#a9a9a9",
      fontFamily: "'GalanoGrotesqueMedium', sans-serif",
    },
  },
};

const fonts = [
  {
    family: "GalanoGrotesqueMedium",
    src: "url('https://www.anddine.co.uk/fonts/GalanoGrotesqueMedium.woff2')",
  },
];

const allowCancel = computed(() => {
  return (
    (props.showCancel || props.isPremiumGroupOrder) &&
    !ordersStore.cartLoading &&
    (props.paymentIntent?.status === "requires_payment_method" ||
      props.paymentIntent?.status === "requires_confirmation")
  );
});

onMounted(() => {
  if (!import.meta.client) return;

  const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/"]');
  if (existingScript) {
    setupElements();
    return;
  }

  const script = document.createElement("script");
  script.src = "https://js.stripe.com/v3/";
  script.async = true;
  script.onload = () => {
    setupElements();
  };
  document.head.appendChild(script);
});

watch(
  () => props.paymentIntent?.client_secret,
  (newSecret, oldSecret) => {
    if (newSecret && newSecret !== oldSecret) {
      setupElements();
    }
  },
);

onUnmounted(() => {
  teardownElements();
});

function setupElements() {
  if (!props.paymentIntent?.client_secret) return;
  if (!window.Stripe) return;
  if (!config.public.stripeKey) {
    console.error("Stripe publishable key is not set (NUXT_PUBLIC_STRIPE_KEY)");
    return;
  }

  teardownElements();

  stripe = window.Stripe(config.public.stripeKey as string);
  elements = (stripe as any).elements({
    clientSecret: props.paymentIntent.client_secret,
    appearance,
    fonts,
  });

  paymentElement = (elements as any).create("payment", {
    paymentMethodOrder: ["google_pay", "apple_pay", "card"],
    wallets: { link: "never" },
  });

  if (stripeElementsRef.value) {
    (paymentElement as any).mount(stripeElementsRef.value);
  }

  (paymentElement as any).on("change", (event: { complete: boolean }) => {
    stripeReady.value = event.complete;
  });
}

function teardownElements() {
  if (paymentElement) {
    try {
      (paymentElement as any).destroy();
    } catch {
      // Element may already be destroyed
    }
    paymentElement = null;
  }
  elements = null;
  stripeReady.value = false;
}

async function handlePayment() {
  if (!stripeReady.value || !stripe || !elements) return;

  error.value = "";


    // The wrapper component (StripePaymentWrapper) drives confirmation
    // tracking via Stripe PI status now — this elements component just
    // submits the payment and emits the result. Confirmation lifecycle
    // (set "started" / poll until succeeded / etc.) is the wrapper's job.

  try {
    const res = await ordersStore.stripePayment({
      stripe,
      elements,
      bookDelivery: props.bookDelivery,
    });
    if ((res as any)?.paymentIntent?.status === "succeeded") {
      emit("payment:confirmed", (res as any).paymentIntent.id);
      emit("paymentSuccess", (res as any).paymentIntent);
    }
  } catch (e) {
    error.value = (e as Error)?.message || "Payment failed";
    emit("paymentError", error.value);
    setTimeout(() => {
      ordersStore.setCheckoutError(false);
    }, 4000);
  }
}

function handleCancel() {
  if (!allowCancel.value) return;
  emit("payment:cancel");
}

defineExpose({ handlePayment });
</script>

<template>
  <section class="stripe-payment">
    <div ref="stripeElementsRef" />
    <DynamicButton
      class="stripe-payment__pay-button"
      color="pink"
      :icon="ordersStore.cartLoading ? 'spinner' : 'lock'"
      :text="`Pay £${((paymentIntent.amount || 0) / 100).toFixed(2)}`"
      :spinning="ordersStore.cartLoading"
      :disabled="!stripeReady || ordersStore.cartLoading"
      width="100%"
      @click-event="handlePayment"
    />
    <DynamicButton
      v-if="showCancel || isPremiumGroupOrder"
      class="stripe-payment__cancel-button"
      color="transparent-black"
      :icon="ordersStore.cartLoading ? 'spinner' : ''"
      text="Cancel"
      :spinning="ordersStore.cartLoading"
      :disabled="!allowCancel"
      width="100%"
      @click-event="handleCancel"
    />
    <BannerError :message="error" />
  </section>
</template>

<style lang="css" scoped>
.stripe-payment {
  width: 100%;
  margin: 1rem 0 0;
}

.stripe-payment__pay-button,
.stripe-payment__cancel-button {
  margin: 0.5rem 0;
}
</style>
